import { generateTypes } from '@evmos/eip712';
import { TxContext, TxPayload, createTransactionPayload } from '@evmos/transactions';
import { EIP712TypedData } from '@evmos/transactions/dist/messages/base';
import { fromDuration, fromTimestamp, toDuration, toTimestamp } from 'cosmjs-types/helpers';
import { MsgCreateBalancerPool } from '../ts-client/pool-models/balancer/tx';
import { PoolAsset } from '../ts-client/pool-models/balancer/balancerPool';
import { MsgCreateBalancerPool as MsgCreateBalancerPoolProto } from '../proto/tx_create_pool_pb';
import { PoolAsset as PoolAssetProto, PoolParams as PoolParamsProto } from '../proto/balancerPool_pb';
import { Coin } from '../proto/coin_pb';
import { toFixed } from '@cosmjs/math';

const CREATE_MSG_CREATE_POOL_TYPES = (params: MsgCreateBalancerPool) => ({
  MsgValue: [
    { name: 'sender', type: 'string' },
    { name: 'pool_params', type: 'TypePoolParams' },
    { name: 'pool_assets', type: 'TypePoolAssets[]' },
    ...(params.futurePoolGovernor ? [ { name: 'future_pool_governor', type: 'string' } ] : []),
  ],
  TypePoolParams: [
    { name: 'swap_fee', type: 'string' },
    { name: 'exit_fee', type: 'string' },
    ...(params.poolParams?.smoothWeightChangeParams ?
      [ { name: 'smooth_weight_change_params', type: 'TypeSmoothWeightChangeParams' } ] :
      []),
  ],
  ...(params.poolParams?.smoothWeightChangeParams ? {
    TypeSmoothWeightChangeParams: [
      { name: 'start_time', type: 'int64' },
      { name: 'duration', type: 'int64' },
      { name: 'initial_pool_weights', type: 'TypePoolAssets[]' },
      { name: 'target_pool_weights', type: 'TypePoolAssets[]' },
    ],
  } : undefined),
  TypePoolAssets: [
    { name: 'token', type: 'TypePoolAssetsToken' },
    { name: 'weight', type: 'string' },
  ],
  TypePoolAssetsToken: [
    { name: 'denom', type: 'string' },
    { name: 'amount', type: 'string' },
  ],
});

export const createTxMsgCreateBalancerPool = (context: TxContext, params: MsgCreateBalancerPool): TxPayload => {
  const typedData = createEIP712MsgCreateBalancerPool(context, params);
  const cosmosMsg = createCosmosMsgCreateBalancerPool(context, params);
  return createTransactionPayload(context, typedData, cosmosMsg);
};

const createEIP712MsgCreateBalancerPool = (context: TxContext, params: MsgCreateBalancerPool): EIP712TypedData => {
  const types = generateTypes(CREATE_MSG_CREATE_POOL_TYPES(params));
  const message = createMsgCreateBalancerPool(params);
  return { types, message };
};

const createCosmosMsgCreateBalancerPool = (context: TxContext, params: MsgCreateBalancerPool): any => {
  const message = new MsgCreateBalancerPoolProto({
    sender: params.sender,
    poolParams: new PoolParamsProto({
      swapFee: params.poolParams?.swapFee,
      exitFee: params.poolParams?.exitFee,
      smoothWeightChangeParams: !params.poolParams?.smoothWeightChangeParams ? undefined : {
        startTime: params.poolParams.smoothWeightChangeParams.startTime &&
          toTimestamp(params.poolParams.smoothWeightChangeParams.startTime),
        duration: params.poolParams.smoothWeightChangeParams.duration,
        initialPoolWeights: params.poolParams.smoothWeightChangeParams.initialPoolWeights
          .map((asset) => new PoolAssetProto({ token: new Coin({ ...asset.token }), weight: asset.weight })),
        targetPoolWeights: params.poolParams.smoothWeightChangeParams.targetPoolWeights
          .map((asset) => new PoolAssetProto({ token: new Coin({ ...asset.token }), weight: asset.weight })),
      },
    }),
    poolAssets: params.poolAssets.map((asset) => new PoolAssetProto({
      token: new Coin({ ...asset.token }),
      weight: asset.weight,
    })),
    futurePoolGovernor: params.futurePoolGovernor,
  });
  return { message, path: MsgCreateBalancerPoolProto.typeName };
};

export function createMsgCreateBalancerPool(params: MsgCreateBalancerPool): object | object[] {
  return {
    type: 'dymensionxyz/dymension/gamm/CreateBalancerPool',
    value: {
      sender: params.sender,
      pool_params: {
        swap_fee: toFixed((Number(params.poolParams?.swapFee) || 0) / Math.pow(10, 18)),
        exit_fee: toFixed((Number(params.poolParams?.exitFee) || 0) / Math.pow(10, 18)),
        ...(params.poolParams?.smoothWeightChangeParams ? {
          smooth_weight_change_params: {
            start_time: !params.poolParams?.smoothWeightChangeParams?.startTime ? '0' :
              fromDuration(toTimestamp(params.poolParams.smoothWeightChangeParams.startTime)),
            duration: !params.poolParams?.smoothWeightChangeParams?.duration ? '0' :
              fromDuration(params.poolParams.smoothWeightChangeParams.duration),
            initial_pool_weights: params.poolParams?.smoothWeightChangeParams?.initialPoolWeights || [],
            target_pool_weights: params.poolParams?.smoothWeightChangeParams?.initialPoolWeights || [],
          },
        } : {}),
      },
      pool_assets: params.poolAssets,
      ...(params.futurePoolGovernor ? { future_pool_governor: params.futurePoolGovernor } : {}),
    },
  };
}

